import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import Icon from 'components/Icon';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {FC, useState} from 'react';
import {useBackend} from 'state/backend';
import {c, t} from 'ttag';
import {documentTypeLabelByType} from 'utils/document-type';

import {ApprovalSettingsFlowsList} from '../components/ApprovalFlowsList';
import ModalFlowCreate from '../components/ModalFlowCreate';
import {BackendData} from '../types';
import {checker} from '../utils';

const links = {
    whatIsApprovalFunction:
        'https://help.makeleaps.jp/ja/articles/3941593-承認機能とは',
};

const useHasApprovalFlowsForAll = () => {
    const {data} = useBackend<BackendData>();
    return data.approvalFlows.some((flow) => flow.documentType === '');
};

type DocumentType = 'all' | keyof BackendData['data']['approvalSettings'];

const DocumentTypeSection: FC<{type: DocumentType}> = ({type}) => {
    const [showApprovalFlows, setShowApprovalFlows] = useState(false);
    const {data} = useBackend<BackendData>();
    const checkers = checker();
    const approvalFlows = data.approvalFlows.filter((flow) => {
        if (type === 'all') {
            return flow.documentType === '';
        } else {
            return flow.documentType === type;
        }
    });
    const hasApprovalFlows = approvalFlows.length > 0;

    let value = false;
    if (type !== 'all') {
        value = data.approvalSettings[type] || false;
    }

    const icon =
        type === 'all' ? (
            <div className="w-4" />
        ) : (
            <Icon
                icon={value ? 'enabled_checkbox' : 'disabled_checkbox'}
                size="base"
            />
        );

    const revokedLabel = checkers.flowHasRevokedUser(
        data.users,
        approvalFlows,
    ) ? (
        <TooltipIcon
            className="text-gold-700"
            icon="warning"
            message={c('approval-settings').t`Contains Revoked User`}
            size="base"
        />
    ) : null;

    return (
        <div className="flex flex-col gap-2" id={`document-type-${type}`}>
            <div className="flex">
                <div className="inline-flex flex-1 items-center gap-2">
                    {icon}
                    {documentTypeLabelByType(type)}
                    {revokedLabel}
                </div>
                {hasApprovalFlows && (
                    <Button
                        data-testid="toggle-flows"
                        kind="secondary"
                        onClick={() => setShowApprovalFlows(!showApprovalFlows)}
                        size="xs"
                    >
                        {showApprovalFlows
                            ? t`Hide`
                            : c('approval-settings').t`Show Flows`}
                    </Button>
                )}
            </div>
            {approvalFlows.length > 0 && showApprovalFlows && (
                <ApprovalSettingsFlowsList
                    documentType={type === 'all' ? '' : type}
                />
            )}
        </div>
    );
};

const ApprovalSettingsSection = () => {
    const {data} = useBackend<BackendData>();
    return (
        <div className="mt-4 flex gap-2">
            <div className="max-w-40 flex-1 border-r border-r-grey-300 pr-2 text-right text-base text-light-secondary">{c(
                'approval-settings',
            ).t`Approval Settings`}</div>
            <div className="flex flex-1 flex-col gap-2">
                <div
                    className="inline-flex flex-1  items-center gap-2"
                    id="approval-settings-is-editing-on-approval-disabled"
                >
                    <Icon
                        icon={
                            data.approvalSettings.isEditingOnApprovalDisabled
                                ? 'enabled_checkbox'
                                : 'disabled_checkbox'
                        }
                        size="base"
                    />
                    {c('approval-settings')
                        .t`Disable Editing of Approved Documents`}
                    <TooltipIcon
                        href={links.whatIsApprovalFunction}
                        message={c('approval-settings')
                            .t`This setting also affects Documents with pending Approval Requests. Click to read more`}
                        size="base"
                    />
                </div>
                <div
                    className="inline-flex flex-1  items-center gap-2"
                    id="approval-settings-editing-on-approval-request-disabled"
                >
                    <Icon
                        icon={
                            data.approvalSettings
                                .editingOnApprovalRequestDisabled
                                ? 'enabled_checkbox'
                                : 'disabled_checkbox'
                        }
                        size="base"
                    />
                    {c('approval-settings')
                        .t`Disable Editing of Documents with Approval Requests`}
                </div>
                <div
                    className="inline-flex flex-1  items-center gap-2"
                    id="approval-settings-self-approval-disabled"
                >
                    <Icon
                        icon={
                            data.approvalSettings.selfApprovalDisabled
                                ? 'enabled_checkbox'
                                : 'disabled_checkbox'
                        }
                        size="base"
                    />
                    {c('approval-settings')
                        .t`Prevent Direct Approval by the Document's creator`}
                    <TooltipIcon
                        href={links.whatIsApprovalFunction}
                        message={c('approval-settings')
                            .t`Not applied when using Approval Flow`}
                        size="base"
                    />
                </div>
                <div
                    className="inline-flex flex-1  items-center gap-2"
                    id="approval-settings-direct-approval-disabled"
                >
                    <Icon
                        icon={
                            data.approvalSettings.directApprovalDisabled
                                ? 'enabled_checkbox'
                                : 'disabled_checkbox'
                        }
                        size="base"
                    />
                    {c('approval-settings')
                        .t`Prevent all Direct Approvals of Documents`}
                    <TooltipIcon
                        href={links.whatIsApprovalFunction}
                        message={c('approval-settings')
                            .t`Must use Approval Flow`}
                        size="base"
                    />
                </div>
            </div>
        </div>
    );
};
const Page = () => {
    const hasAlApprovalFlowsForAllDocumentTypes = useHasApprovalFlowsForAll();
    return (
        <div>
            <DjangoMessages className="mb-4" />
            <NavigationTabs.DocumentSettings selection="documentApproval" />
            <SectionHeader
                subtitle={c('approval-settings')
                    .t`Select document types for which approval is needed before sending by Japan Post or Secure Send`}
                title={c('approval-settings').t`Approval Settings`}
            />
            <div className="mt-2 flex flex-row-reverse gap-2">
                <ModalFlowCreate />
                <LinkButton
                    href={window.Urls.approval_settings_edit_view()}
                    kind="secondary"
                    size="sm"
                >{c('approval-settings').t`Edit Approval Settings`}</LinkButton>
            </div>
            <div className="mt-6 flex gap-2">
                <div className="max-w-40 flex-1 border-r border-r-grey-300 pr-2 text-right text-base text-light-secondary">{t`Documents Types`}</div>
                <div className="flex flex-1 flex-col gap-2">
                    <DocumentTypeSection type="order" />
                    <DocumentTypeSection type="quote" />
                    <DocumentTypeSection type="orderslip" />
                    <DocumentTypeSection type="orderconfirmation" />
                    <DocumentTypeSection type="timesheet" />
                    <DocumentTypeSection type="deliveryslip" />
                    <DocumentTypeSection type="acceptance" />
                    <DocumentTypeSection type="paymentnotice" />
                    <DocumentTypeSection type="invoice" />
                    <DocumentTypeSection type="receipt" />
                    {hasAlApprovalFlowsForAllDocumentTypes && (
                        <DocumentTypeSection type="all" />
                    )}
                </div>
            </div>
            <hr />
            <ApprovalSettingsSection />
        </div>
    );
};
export default Page;
